import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

import HomeIcon from '@mui/icons-material/Home';
import RoomIcon from '@mui/icons-material/Room';
import PersonIcon from '@mui/icons-material/Person';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { NavLink, useLocation } from 'react-router-dom';
import { useRef, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from 'src/content/auth/slice';
import { setExternalLink } from 'src/content/pages/IframeViewer/slice';
import { useNavigate } from 'react-router';

import {
  Box,
  List,
  ListItem,
  IconButton,
  Typography,
  ListItemText,
  useMediaQuery,
  Avatar,
  Popover,
  Divider,
  Button,
  lighten,
  Menu,
  MenuItem,
} from '@mui/material';

const ListWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile'
})(
  //@ts-ignore
  ({ theme, isMobile }) => `
        .MuiTouchRipple-root {
            display: none;
        }
        
        .MuiListItem-root {
            transition: ${theme.transitions.create(['color', 'fill', 'background-color'])};
            border-radius: ${theme.general.borderRadiusXXl};
            margin: ${theme.spacing(0, 0.5)};
            
            &.MuiListItem-indicators {
                padding: ${theme.spacing(1, 2)};
            
                &.active,
                &:active,
                &:hover {
                    background-color: ${isMobile ? 'transparent' : theme.colors.ckb.ternary};
                    
                    .MuiListItemText-root {
                        .MuiTypography-root {
                            color: ${theme.colors.ckb.primary};
                        }
                    }
                    
                    .MuiIconButton-root {
                        color: ${theme.colors.ckb.primary};
                    }

                    .MuiTypography-caption {
                        color: ${theme.colors.ckb.primary};
                    }
                }
            }
        }
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)}
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

// Array of Material UI colors to choose from
const colorOptions = [
  'red', 'pink', 'purple', 'deepPurple', 'indigo', 'blue',
  'lightBlue', 'cyan', 'teal', 'green', 'lightGreen', 'lime',
  'yellow', 'amber', 'orange', 'deepOrange'
];

// Function to generate a consistent color based on name
const getAvatarColor = (name) => {
  if (!name) return '#673ab7'; // Default color (deepPurple[500])

  // Generate a number from the name string
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Use the hash to pick a color
  const colorIndex = Math.abs(hash) % colorOptions.length;

  return colorOptions[colorIndex];
};

function HeaderMenu() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  //@ts-ignore
  const { fullName, consignees, roleMenuList = [] } = useSelector((state) => state.Auth.user) || { fullName: 'User', roleMenuList: [] };

  const user = {
    name: fullName,
    jobtitle: consignees?.length > 0 ? consignees[0]?.customer_name : '-'
  };

  // Check if Admin menu exists in roleMenuList
  const adminMenu = useMemo(() =>
    roleMenuList.find(menu => menu.menu === 'Admin'),
    [roleMenuList]
  );

  // Filter out Admin menu from dashboard menus
  const dashboardMenus = useMemo(() =>
    roleMenuList.filter(menu => menu.menu !== 'Admin'),
    [roleMenuList]
  );

  // Memoize the avatar color so it doesn't change on re-renders
  const avatarColor = useMemo(() => getAvatarColor(fullName), [fullName]);
  const avatarInitial = fullName ? fullName[0] : 'U';

  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  // State for additional menu dropdowns
  const [menuAnchors, setMenuAnchors] = useState<{ [key: string]: HTMLElement | null }>({});

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleSignOut = () => {
    dispatch(logout());
    navigate('/');
    handleClose();
  };

  const isActive = (path: string) => location.pathname === path;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, menuName: string) => {
    setMenuAnchors({ ...menuAnchors, [menuName]: event.currentTarget });
  };

  const handleMenuClose = (menuName: string) => {
    setMenuAnchors({ ...menuAnchors, [menuName]: null });
  };

  const handleSubmenuClick = (menuName: string, submenuName: string, link: string) => {
    const title = `${menuName} - ${submenuName}`;
    dispatch(setExternalLink({ url: link, title }));
    navigate(`/external/${encodeURIComponent(title)}`);
    handleMenuClose(menuName);
  };

  const handleAdminSubmenuClick = (submenuName: string) => {
    handleMenuClose('Admin');
    // Map submenu names to routes
    if (submenuName === 'User Management') {
      navigate('/admin/users');
    } else if (submenuName === 'Role Management') {
      navigate('/admin/roles');
    } else if (submenuName === 'Menu Management') {
      navigate('/admin/menus');
    }
  };

  return (
    <>
      <ListWrapper
        //@ts-ignore
        isMobile={isMobile}
        sx={{
          display: {
            xs: 'flex',
            md: 'flex'
          },
          width: '100%',
          maxWidth: '100%',
          overflow: 'visible',
          justifyContent: 'center',
        }}
      >
        <List
          disablePadding
          component={Box}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'center',
            // width: '100%',
            overflowX: 'auto',
            overflowY: 'visible',
            '&::-webkit-scrollbar': {
              height: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.05)',
              borderRadius: '3px',
            },
            // Smooth scrolling
            scrollBehavior: 'smooth',
            // Allow scroll on hover
            '&:hover': {
              overflowX: 'auto',
            }
          }}
        >
          <ListItem
            button
            component={NavLink}
            to="/dashboards/summary"
            classes={{ root: 'MuiListItem-indicators' }}
          >
            {isMobile ? (
              <Box display="flex" flexDirection="column" alignItems="center">
                <IconButton
                  sx={{
                    color: isActive('/dashboards/summary') ? theme.colors.ckb.error : 'inherit',
                  }}
                >
                  <HomeIcon />
                </IconButton>
                <Typography sx={{ fontSize: 10 }} variant="caption">Home</Typography>
              </Box>
            ) : (
              <ListItemText
                primaryTypographyProps={{ noWrap: true }}
                primary="Home"
              />
            )}
          </ListItem>
          <ListItem
            button
            component={NavLink}
            to="/management/transactions"
            classes={{ root: 'MuiListItem-indicators' }}
          >
            {isMobile ? (
              <Box display="flex" flexDirection="column" alignItems="center">
                <IconButton
                  sx={{
                    color: isActive('/management/transactions') ? 'white' : 'inherit',
                  }}
                >
                  <RoomIcon />
                </IconButton>
                <Typography sx={{ fontSize: 10 }} variant="caption">Tracking</Typography>
              </Box>
            ) : (
              <ListItemText
                primaryTypographyProps={{ noWrap: true }}
                primary="Tracing & Tracking"
              />
            )}
          </ListItem>

          {/* Dashboard Menu - Groups all roleMenuList items (excluding Admin) */}
          {!isMobile && dashboardMenus.length > 0 && (
            <ListItem
              button
              onClick={(e) => handleMenuOpen(e, 'Dashboard')}
              classes={{ root: 'MuiListItem-indicators' }}
            >
              <ListItemText
                primaryTypographyProps={{ noWrap: true }}
                primary="Dashboard"
              />
              <ExpandMoreIcon
                sx={{
                  transform: menuAnchors['Dashboard'] ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: theme.transitions.create(['transform']),
                }}
              />
            </ListItem>
          )}

          {/* Admin Menu - Only show if exists in roleMenuList */}
          {!isMobile && adminMenu && (
            <ListItem
              button
              onClick={(e) => handleMenuOpen(e, 'Admin')}
              classes={{ root: 'MuiListItem-indicators' }}
            >
              <ListItemText
                primaryTypographyProps={{ noWrap: true }}
                primary="Admin"
              />
              <ExpandMoreIcon
                sx={{
                  transform: menuAnchors['Admin'] ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: theme.transitions.create(['transform']),
                }}
              />
            </ListItem>
          )}

          <ListItem
            button
            onClick={handleOpen}
            ref={ref}
            classes={{ root: 'MuiListItem-indicators' }}
          >
            {isMobile ? (
              <Box display="flex" flexDirection="column" alignItems="center">
                <IconButton
                  sx={{
                    color: isOpen ? theme.colors.ckb.primary : 'inherit',
                  }}
                >
                  <PersonIcon />
                </IconButton>
                <Typography sx={{ fontSize: 10 }} variant="caption">Profile</Typography>
              </Box>
            ) : (
              <></>
            )}
          </ListItem>
        </List>
      </ListWrapper>

      {/* Dashboard Menu Dropdown - Shows all dashboard items (excluding Admin) */}
      <Menu
        anchorEl={menuAnchors['Dashboard']}
        open={Boolean(menuAnchors['Dashboard'])}
        onClose={() => handleMenuClose('Dashboard')}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 250,
          }
        }}
      >
        {dashboardMenus.map((menuItem, menuIndex) => (
          <Box key={menuItem.menu}>
            {menuIndex > 0 && <Divider />}
            <MenuItem
              disabled
              sx={{
                opacity: '1 !important',
                fontWeight: 'bold',
                color: theme.palette.text.primary,
                cursor: 'default',
                '&:hover': {
                  bgcolor: 'transparent',
                }
              }}
            >
              {menuItem?.menu || ''}
            </MenuItem>
            {[...menuItem.submenus].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })).map((submenu) => (
              <MenuItem
                key={submenu.name}
                onClick={() => {
                  handleSubmenuClick(menuItem.menu, submenu.name, submenu.link);
                  handleMenuClose('Dashboard');
                }}
                sx={{
                  pl: 4,
                  '&:hover': {
                    bgcolor: theme.colors.ckb.ternary,
                    color: theme.colors.ckb.primary,
                  }
                }}
              >
                {submenu.name}
              </MenuItem>
            ))}
          </Box>
        ))}
      </Menu>

      {/* Admin Menu Dropdown - Dynamically rendered from roleMenuList */}
      {adminMenu && (
        <Menu
          anchorEl={menuAnchors['Admin']}
          open={Boolean(menuAnchors['Admin'])}
          onClose={() => handleMenuClose('Admin')}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
            }
          }}
        >
          {[...adminMenu.submenus].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })).map((submenu) => (
            <MenuItem
              key={submenu.name}
              onClick={() => handleAdminSubmenuClick(submenu.name)}
              sx={{
                '&:hover': {
                  bgcolor: theme.colors.ckb.ternary,
                  color: theme.colors.ckb.primary,
                }
              }}
            >
              {submenu.name}
            </MenuItem>
          ))}
        </Menu>
      )}

      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          <Avatar sx={{ bgcolor: avatarColor }}>{avatarInitial}</Avatar>
          <UserBoxText>
            <UserBoxLabel variant="body1">{user.name}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {user.jobtitle}
            </UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider />
        <Box sx={{ m: 1 }}>
          <Button color="primary" fullWidth onClick={handleSignOut} sx={{ color: theme.colors.ckb.error }}>
            <LockOpenTwoToneIcon sx={{ mr: 1, color: theme.colors.ckb.error }} />
            Sign out
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderMenu;
